import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Plus, X, ChevronRight, ChevronDown, ExternalLink, Search, Trash2,
  CheckCircle, AlertCircle, ArrowLeft, Edit2
} from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { format, formatDistanceToNow, differenceInDays } from 'date-fns'
import { Q2StepEditor, getQ2StepType } from './Q2StepEditors'
import { AEOGridTab } from './AEOGridTab'
import { isActiveProfile } from './constants'

const safeFmt = (dateStr, fmt) => {
  if (!dateStr) return null
  try {
    const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T12:00:00')
    if (isNaN(d.getTime())) return null
    return format(d, fmt)
  } catch { return null }
}

const YOAST_RATINGS = [
  { value: '',              label: '—',               color: '#94a3b8', bg: '#f1f5f9' },
  { value: 'red',           label: 'Red',             color: '#c94f4f', bg: '#fdf0f0' },
  { value: 'orange',        label: 'Orange',          color: '#c4962a', bg: '#fff8e1' },
  { value: 'green',         label: 'Green',           color: '#2e6e3a', bg: '#e8f5eb' },
  { value: 'do_not_change', label: 'Do Not Change',   color: '#7b5ea8', bg: '#f0eef8' },
]
const getRating = v => YOAST_RATINGS.find(r => r.value === (v || ''))

export const QUARTERLY_AUDIT_TEMPLATE_ID = 'tpl-quarterly-audit'

export default function SEOWorkspaceView({ clients, profiles, currentUser, profile, selectedSeoClientId, selectedSeoProjectId, onClearSeoClientId, onOpenTask }) {
  const [selectedClient, setSelectedClient] = useState(null)
  const [autoOpenProjectId, setAutoOpenProjectId] = useState(null)

  useEffect(() => {
    if (selectedSeoClientId && clients.length > 0) {
      const c = clients.find(x => x.id === selectedSeoClientId)
      if (c) {
        setSelectedClient(c)
        setAutoOpenProjectId(selectedSeoProjectId || null)
        onClearSeoClientId && onClearSeoClientId()
      }
    }
  }, [selectedSeoClientId, selectedSeoProjectId, clients, onClearSeoClientId])

  if (selectedClient) {
    return <ClientSEOWorkspace
      client={selectedClient}
      profiles={profiles}
      currentUser={currentUser}
      autoOpenProjectId={autoOpenProjectId}
      onClearAutoOpenProjectId={() => setAutoOpenProjectId(null)}
      onOpenTask={onOpenTask}
      onBack={() => { setSelectedClient(null); setAutoOpenProjectId(null) }}
    />
  }

  return <ClientListView clients={clients} onSelect={c => setSelectedClient(c)} />
}

function ClientListView({ clients, onSelect }) {
  const [search, setSearch] = useState('')
  // Which clients actually have SEO: the has_seo service flag, OR any SEO project / Yoast page.
  const [seoIds, setSeoIds] = useState(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      supabase.from('seo_pages').select('client_id'),
      supabase.from('seo_projects').select('client_id'),
    ]).then(([pages, projects]) => {
      if (cancelled) return
      const ids = new Set()
      ;(pages.data || []).forEach(r => r.client_id && ids.add(r.client_id))
      ;(projects.data || []).forEach(r => r.client_id && ids.add(r.client_id))
      setSeoIds(ids)
    })
    return () => { cancelled = true }
  }, [])

  const seoClients = clients
    .filter(c => !c.archived_at && (c.has_seo || (seoIds && seoIds.has(c.id))))
    .filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))

  return (
    <div>
      <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-muted)' }}>
        Clients with SEO — pick one to manage setup, page optimization, projects, and audits.
      </div>
      <div className="filters-bar" style={{ marginBottom: 16 }}>
        <input className="search-input" placeholder="Search SEO clients..." value={search} onChange={e => setSearch(e.target.value)} />
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>{seoClients.length} clients</span>
      </div>
      {seoIds === null ? (
        <div style={{ color: 'var(--text-muted)', padding: 24 }}>Loading…</div>
      ) : seoClients.length === 0 ? (
        <div className="empty-state">No clients with SEO {search ? 'match your search' : 'yet'}.</div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="task-table">
            <thead><tr><th>Client</th><th>Website</th></tr></thead>
            <tbody>
              {seoClients.map(c => (
                <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => onSelect(c)}>
                  <td style={{ fontWeight: 600 }}>{c.name}</td>
                  <td>{c.website
                    ? <a href={c.website} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }} onClick={e => e.stopPropagation()}>{c.website}</a>
                    : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function ClientSEOWorkspace({ client, profiles, currentUser, onBack, autoOpenProjectId, onClearAutoOpenProjectId, onOpenTask }) {
  const [activeTab, setActiveTab] = useState(autoOpenProjectId ? 'projects' : 'overview')
  const [pages, setPages] = useState([])
  const [projects, setProjects] = useState([])
  const [templates, setTemplates] = useState([])
  const [setupTask, setSetupTask] = useState(null)
  const [initialLoading, setInitialLoading] = useState(true)
  const toast = useToast()

  const load = useCallback(async () => {
    const [{ data: ps }, { data: prs }, { data: tpls }, { data: setup }] = await Promise.all([
      supabase.from('seo_pages').select('*').eq('client_id', client.id).order('sort_order').order('page_name'),
      supabase.from('seo_projects').select('*, items:seo_project_items(*)').eq('client_id', client.id).order('created_at', { ascending: false }),
      supabase.from('seo_project_templates').select('*'),
      supabase.from('tasks').select('*, comment_count:task_comments(count)').eq('client_id', client.id).eq('template_id', 'std-internal-seo-checklist').maybeSingle(),
    ])
    setPages(ps || [])
    setProjects(prs || [])
    setTemplates(tpls || [])
    setSetupTask(setup || null)
    setInitialLoading(false)
  }, [client.id])

  useEffect(() => { load() }, [load])

  const lastAudit = useMemo(() => {
    const audits = projects.filter(p => p.template_id === QUARTERLY_AUDIT_TEMPLATE_ID && p.status === 'complete')
    if (audits.length === 0) return null
    return audits.sort((a, b) => new Date(b.completed_at || b.updated_at) - new Date(a.completed_at || a.updated_at))[0]
  }, [projects])

  const auditAlert = useMemo(() => {
    const activeAudit = projects.find(p => p.template_id === QUARTERLY_AUDIT_TEMPLATE_ID && p.status !== 'complete')
    if (activeAudit) return { level: 'info', message: `Quarterly audit currently in progress (${activeAudit.name})` }
    if (!lastAudit) return { level: 'warning', message: 'No quarterly audit has ever been done for this client' }
    const days = differenceInDays(new Date(), new Date(lastAudit.completed_at || lastAudit.updated_at))
    if (days >= 90) return { level: 'warning', message: `Last quarterly audit was ${days} days ago — overdue` }
    if (days >= 75) return { level: 'info', message: `Last quarterly audit was ${days} days ago — due soon` }
    return null
  }, [lastAudit, projects])

  const TABS = [
    { id: 'overview',  label: 'Overview' },
    { id: 'pages',     label: `Yoast Pages (${pages.length})` },
    { id: 'projects',  label: `Projects (${projects.length})` },
    { id: 'aeo',       label: 'AEO Grids' },
    { id: 'audits',    label: 'Audit History' },
  ]

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}><ArrowLeft size={14} /> Back to clients</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--navy)' }}>{client.name} · SEO</h1>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {client.website && <a href={client.website} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }}>{client.website}</a>}
          </div>
        </div>
      </div>

      {auditAlert && (
        <div style={{
          background: auditAlert.level === 'warning' ? '#fdf0f0' : '#fff8e1',
          border: `1.5px solid ${auditAlert.level === 'warning' ? '#f0c4c4' : '#f0d98a'}`,
          borderRadius: 10, padding: '10px 14px', marginBottom: 16,
          color: auditAlert.level === 'warning' ? '#8a3a3a' : '#8a6000',
          fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertCircle size={14} /> {auditAlert.message}
        </div>
      )}

      <div className="tabs" style={{ marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t.id} className={`tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {initialLoading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
      ) : (
        <>
          {activeTab === 'overview' && (
            <OverviewTab pages={pages} projects={projects} lastAudit={lastAudit} setActiveTab={setActiveTab} setupTask={setupTask} profiles={profiles} onOpenTask={onOpenTask} />
          )}
          {activeTab === 'pages' && (
            <PagesTab client={client} pages={pages} profiles={profiles} onReload={load} />
          )}
          {activeTab === 'projects' && (
            <ProjectsTab client={client} projects={projects} templates={templates} profiles={profiles} currentUser={currentUser} onReload={load} autoOpenProjectId={autoOpenProjectId} onClearAutoOpenProjectId={onClearAutoOpenProjectId} />
          )}
          {activeTab === 'aeo' && (
            <AEOGridTab client={client} profiles={profiles} currentUser={currentUser} />
          )}
          {activeTab === 'audits' && (
            <AuditsTab projects={projects} />
          )}
        </>
      )}
    </div>
  )
}

function OverviewTab({ pages, projects, lastAudit, setActiveTab, setupTask, profiles, onOpenTask }) {
  const greenPages = pages.filter(p => p.yoast_rating === 'green').length
  const submittedPages = pages.filter(p => p.submitted_to_gsc).length
  const activeProjects = projects.filter(p => p.status === 'active').length
  const completeProjects = projects.filter(p => p.status === 'complete').length

  const setupItems = setupTask?.checklist_items || []
  const setupDone = setupItems.filter(i => i.checked).length
  const setupTotal = setupItems.length
  const setupAssignee = setupTask && profiles?.find(p => p.id === setupTask.assigned_to)
  const isSetupComplete = setupTotal > 0 && setupDone === setupTotal
  const setupNotStarted = setupTotal > 0 && setupDone === 0

  return (
    <div>
      {setupTask && setupTotal > 0 && (
        <div className="card" style={{
          marginBottom: 16,
          cursor: onOpenTask ? 'pointer' : 'default',
          borderLeft: `4px solid ${isSetupComplete ? '#2e6e3a' : setupNotStarted ? 'var(--text-muted)' : 'var(--gold)'}`,
        }}
          onClick={() => onOpenTask && onOpenTask(setupTask)}>
          <div className="card-body" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                {isSetupComplete ? '✅' : setupNotStarted ? '🔲' : '⚙️'} SEO Setup Checklist
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>
                  {setupDone} of {setupTotal} steps done
                </span>
              </div>
              <div style={{ height: 6, background: 'var(--cream-dark)', borderRadius: 3, overflow: 'hidden', maxWidth: 320 }}>
                <div style={{
                  height: '100%',
                  width: `${setupTotal ? (setupDone / setupTotal * 100) : 0}%`,
                  background: isSetupComplete ? '#2e6e3a' : 'var(--gold)',
                  transition: 'width 200ms',
                }} />
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                {setupAssignee ? `Assigned to ${setupAssignee.full_name}` : 'Unassigned'}
                {setupTask.date_due && ` · Due ${safeFmt(setupTask.date_due, 'MMM d, yyyy')}`}
              </div>
            </div>
            {onOpenTask && (
              <span style={{ fontSize: 11.5, color: 'var(--teal)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                Open task →
              </span>
            )}
          </div>
        </div>
      )}

      {!setupTask && (
        <div style={{ background: 'var(--cream)', border: '1px solid var(--border-light)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
          <strong>No SEO setup task</strong> has been created for this client yet. Run the Kickoff Builder (or create an "Internal SEO Checklist" task manually) to start tracking setup progress here.
        </div>
      )}

      <div className="stats-row" style={{ marginBottom: 20 }}>
        {[
          { label: 'Pages tracked',    value: pages.length, color: 'var(--navy-mid)' },
          { label: 'Green-rated',      value: greenPages, color: '#2e6e3a' },
          { label: 'Submitted to GSC', value: submittedPages, color: 'var(--teal)' },
          { label: 'Active projects',  value: activeProjects, color: 'var(--gold)' },
          { label: 'Done projects',    value: completeProjects, color: 'var(--mauve)' },
        ].map(s => (
          <div key={s.label} className="stat-card" style={{ display: 'flex', alignItems: 'center' }}>
            <div className="stat-accent" style={{ background: s.color }} />
            <div>
              <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {lastAudit && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><span className="card-title">Last Quarterly Audit</span></div>
          <div className="card-body">
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{lastAudit.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Completed {safeFmt(lastAudit.completed_at || lastAudit.updated_at, 'MMM d, yyyy')}
              {lastAudit.score_start !== null && lastAudit.score_finish !== null && lastAudit.score_start !== undefined && lastAudit.score_finish !== undefined && (
                <> · AHREF score: {lastAudit.score_start} → <strong>{lastAudit.score_finish}</strong></>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-ghost" onClick={() => setActiveTab('pages')}>Manage Yoast Pages →</button>
        <button className="btn btn-ghost" onClick={() => setActiveTab('projects')}>Manage Projects →</button>
      </div>
    </div>
  )
}

function PagesTab({ client, pages, profiles, onReload }) {
  const toast = useToast()
  const [showAdd, setShowAdd] = useState(false)
  const [showBulkAdd, setShowBulkAdd] = useState(false)
  const [editingPage, setEditingPage] = useState(null)
  const [selectedPageIds, setSelectedPageIds] = useState([])
  const [search, setSearch] = useState('')
  const [filterRating, setFilterRating] = useState('')

  const filtered = pages.filter(p => {
    if (search && !((p.page_name || '').toLowerCase().includes(search.toLowerCase()) || (p.keyword_choice || '').toLowerCase().includes(search.toLowerCase()))) return false
    if (filterRating && p.yoast_rating !== filterRating) return false
    return true
  })

  const toggleFlag = async (page, field) => {
    const next = !page[field]
    await supabase.from('seo_pages').update({ [field]: next }).eq('id', page.id)
    onReload()
  }

  const setRating = async (page, rating) => {
    await supabase.from('seo_pages').update({ yoast_rating: rating }).eq('id', page.id)
    onReload()
  }

  const setAssignee = async (page, assigneeId) => {
    await supabase.from('seo_pages').update({ assigned_to: assigneeId || null }).eq('id', page.id)
    onReload()
  }

  return (
    <div>
      <div className="filters-bar" style={{ marginBottom: 14 }}>
        <input className="search-input" placeholder="Search pages..." value={search} onChange={e => setSearch(e.target.value)} />
        <select className="filter-select" value={filterRating} onChange={e => setFilterRating(e.target.value)}>
          <option value="">All ratings</option>
          {YOAST_RATINGS.filter(r => r.value).map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>{filtered.length} pages</span>
        <button className="btn btn-ghost" onClick={() => setShowBulkAdd(true)}><Plus size={14} /> Bulk Add</button>
        <button className="btn btn-gold" onClick={() => setShowAdd(true)}><Plus size={14} /> Add Page</button>
      </div>

      {pages.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize: 48, marginBottom: 12 }}>📄</div>
          <h3>No pages tracked yet</h3>
          <p>Add the pages on this client's site so the team can work through Yoast optimization page by page.</p>
        </div>
      ) : (
        <>
          {selectedPageIds.length > 0 && (
            <div style={{ background: 'var(--navy)', color: '#fff', padding: '10px 16px', borderRadius: 10, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
              <span style={{ fontWeight: 600 }}>{selectedPageIds.length} selected</span>
              <span style={{ opacity: 0.6 }}>·</span>
              <span>Bulk assign to:</span>
              <select onChange={async (e) => {
                if (!e.target.value) return
                await supabase.from('seo_pages').update({ assigned_to: e.target.value }).in('id', selectedPageIds)
                toast(`Assigned ${selectedPageIds.length} pages`, 'success')
                setSelectedPageIds([])
                onReload()
              }} value=""
                style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', padding: '4px 8px', borderRadius: 6, fontSize: 12 }}>
                <option value="">Pick a person...</option>
                {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id} style={{ color: '#000' }}>{p.full_name}</option>)}
              </select>
              <button onClick={async () => {
                if (!window.confirm(`Delete ${selectedPageIds.length} pages?`)) return
                await supabase.from('seo_pages').delete().in('id', selectedPageIds)
                toast(`Deleted ${selectedPageIds.length} pages`, 'success')
                setSelectedPageIds([])
                onReload()
              }} style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                Delete
              </button>
              <button onClick={() => setSelectedPageIds([])} style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                Clear
              </button>
            </div>
          )}
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="task-table" style={{ width: '100%', minWidth: 1100 }}>
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <input type="checkbox"
                    checked={selectedPageIds.length === filtered.length && filtered.length > 0}
                    onChange={e => setSelectedPageIds(e.target.checked ? filtered.map(p => p.id) : [])} />
                </th>
                <th style={{ minWidth: 180 }}>Page</th>
                <th style={{ minWidth: 140 }}>Keyword</th>
                <th title="Alt text — featured image">Alt F</th>
                <th title="Alt text — all on-page images">Alt All</th>
                <th title="Description — featured image">Desc F</th>
                <th title="Meta description">Meta</th>
                <th title="SEO title">Title</th>
                <th title="Slug">Slug</th>
                <th>Rating</th>
                <th title="Submitted to Google Search Console">GSC</th>
                <th>Assignee</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const rating = getRating(p.yoast_rating)
                return (
                  <tr key={p.id} style={{ cursor: 'pointer' }} onClick={e => {
                    if (e.target.tagName === 'TD' || e.target.tagName === 'TR') {
                      setEditingPage(p)
                    }
                  }}>
                    <td onClick={e => e.stopPropagation()}>
                      <input type="checkbox"
                        checked={selectedPageIds.includes(p.id)}
                        onChange={e => setSelectedPageIds(ids => e.target.checked ? [...ids, p.id] : ids.filter(x => x !== p.id))} />
                    </td>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>
                        {p.is_urgent && <span style={{ color: 'var(--red)', marginRight: 4 }}>🔴</span>}
                        {p.page_name}
                      </div>
                      {p.page_url && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}><a href={p.page_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'var(--teal)' }}>↗ open</a></div>}
                    </td>
                    <td><span style={{ fontSize: 12.5 }}>{p.keyword_choice || <span style={{ color: 'var(--text-muted)' }}>—</span>}</span></td>
                    <Cell><Check checked={p.alt_text_featured} onClick={e => { e.stopPropagation(); toggleFlag(p, 'alt_text_featured') }} /></Cell>
                    <Cell><Check checked={p.alt_text_all_images} onClick={e => { e.stopPropagation(); toggleFlag(p, 'alt_text_all_images') }} /></Cell>
                    <Cell><Check checked={p.description_featured} onClick={e => { e.stopPropagation(); toggleFlag(p, 'description_featured') }} /></Cell>
                    <Cell><Check checked={p.meta_description} onClick={e => { e.stopPropagation(); toggleFlag(p, 'meta_description') }} /></Cell>
                    <Cell><Check checked={p.seo_title} onClick={e => { e.stopPropagation(); toggleFlag(p, 'seo_title') }} /></Cell>
                    <Cell><Check checked={p.slug} onClick={e => { e.stopPropagation(); toggleFlag(p, 'slug') }} /></Cell>
                    <td onClick={e => e.stopPropagation()}>
                      <select value={p.yoast_rating || ''} onChange={e => setRating(p, e.target.value)}
                        style={{ fontSize: 11, padding: '3px 6px', border: 'none', background: rating.bg, color: rating.color, borderRadius: 6, fontWeight: 600 }}>
                        {YOAST_RATINGS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </td>
                    <Cell><Check checked={p.submitted_to_gsc} onClick={e => { e.stopPropagation(); toggleFlag(p, 'submitted_to_gsc') }} /></Cell>
                    <td onClick={e => e.stopPropagation()}>
                      <select value={p.assigned_to || ''} onChange={e => setAssignee(p, e.target.value)}
                        style={{ fontSize: 11.5, padding: '3px 6px', border: '1px solid var(--border-light)', borderRadius: 6, background: '#fff', maxWidth: 110 }}>
                        <option value="">Unassigned</option>
                        {profiles.filter(isActiveProfile).map(pf => <option key={pf.id} value={pf.id}>{pf.full_name}</option>)}
                      </select>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <button className="btn-icon" onClick={async () => {
                        if (!window.confirm(`Delete page "${p.page_name}"?`)) return
                        await supabase.from('seo_pages').delete().eq('id', p.id)
                        toast('Page deleted', 'success')
                        onReload()
                      }}><Trash2 size={12} /></button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        </>
      )}

      {showAdd && (
        <PageFormModal
          client={client}
          profiles={profiles}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); onReload() }}
        />
      )}
      {showBulkAdd && (
        <BulkAddPagesModal
          client={client}
          profiles={profiles}
          onClose={() => setShowBulkAdd(false)}
          onSaved={() => { setShowBulkAdd(false); onReload() }}
        />
      )}
      {editingPage && (
        <YoastPageSidePanel
          client={client}
          profiles={profiles}
          page={pages.find(p => p.id === editingPage.id) || editingPage}
          onClose={() => setEditingPage(null)}
          onUpdated={onReload}
        />
      )}
    </div>
  )
}

function Cell({ children }) {
  return <td style={{ textAlign: 'center' }}>{children}</td>
}

function Check({ checked, onClick }) {
  return (
    <button onClick={onClick}
      style={{
        width: 22, height: 22, borderRadius: 6, cursor: 'pointer',
        border: `1.5px solid ${checked ? '#2e6e3a' : 'var(--border)'}`,
        background: checked ? '#2e6e3a' : '#fff', color: '#fff',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
      }}>
      {checked ? '✓' : ''}
    </button>
  )
}

function PageFormModal({ client, profiles, page, onClose, onSaved }) {
  const toast = useToast()
  const isEdit = !!page
  const [form, setForm] = useState({
    page_name: page?.page_name || '',
    page_url: page?.page_url || '',
    keyword_choice: page?.keyword_choice || '',
    final_url: page?.final_url || '',
    category: page?.category || '',
    notes: page?.notes || '',
    is_urgent: page?.is_urgent || false,
    due_date: page?.due_date || '',
    assigned_to: page?.assigned_to || '',
    loom_url: page?.loom_url || '',
    yoast_rating: page?.yoast_rating || '',
    alt_text_featured: page?.alt_text_featured || false,
    alt_text_all_images: page?.alt_text_all_images || false,
    description_featured: page?.description_featured || false,
    meta_description: page?.meta_description || false,
    seo_title: page?.seo_title || false,
    slug: page?.slug || false,
    submitted_to_gsc: page?.submitted_to_gsc || false,
  })
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const save = async () => {
    if (!form.page_name.trim()) { toast('Page name required', 'error'); return }
    setSaving(true)
    const payload = {
      ...form,
      client_id: client.id,
      assigned_to: form.assigned_to || null,
      due_date: form.due_date || null,
      yoast_rating: form.yoast_rating || null,
    }
    const { error } = isEdit
      ? await supabase.from('seo_pages').update(payload).eq('id', page.id)
      : await supabase.from('seo_pages').insert(payload)
    if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }
    toast(isEdit ? 'Page updated' : 'Page added', 'success')
    onSaved()
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 90, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,43,74,0.3)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: 540, background: '#fff', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ background: 'var(--navy)', padding: '18px 22px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 17 }}>{isEdit ? 'Edit Page' : 'Add Page'}</div>
          <button className="btn-icon" style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff' }} onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ padding: 20, flex: 1, overflowY: 'auto' }}>
          <div className="form-group">
            <label className="form-label">Page Name *</label>
            <input className="form-control" value={form.page_name} onChange={e => set('page_name', e.target.value)} placeholder="e.g. About, Anxiety Treatment, Home" />
          </div>
          <div className="form-group">
            <label className="form-label">Page URL</label>
            <input className="form-control" value={form.page_url} onChange={e => set('page_url', e.target.value)} placeholder="https://..." />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Target Keyword</label>
              <input className="form-control" value={form.keyword_choice} onChange={e => set('keyword_choice', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <input className="form-control" value={form.category} onChange={e => set('category', e.target.value)} placeholder="e.g. Main Pages, Landing Pages, Blog" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Assigned To</label>
              <select className="form-control" value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
                <option value="">Unassigned</option>
                {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input className="form-control" type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Loom walkthrough (optional)</label>
            <input className="form-control" value={form.loom_url} onChange={e => set('loom_url', e.target.value)} placeholder="https://www.loom.com/share/... — a video explaining the changes" />
            {form.loom_url && (
              <a href={form.loom_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--teal)', marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <ExternalLink size={11} /> Open Loom
              </a>
            )}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, marginBottom: 16 }}>
            <input type="checkbox" checked={form.is_urgent} onChange={e => set('is_urgent', e.target.checked)} />
            🔴 Mark as urgent
          </label>

          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Optimization Status</div>
          {[
            ['alt_text_featured',    'Alt text — featured image'],
            ['alt_text_all_images',  'Alt text — all on-page images'],
            ['description_featured', 'Description — featured image'],
            ['meta_description',     'Meta description'],
            ['seo_title',            'SEO title'],
            ['slug',                 'Slug optimized'],
            ['submitted_to_gsc',     'Submitted to Google Search Console'],
          ].map(([k, label]) => (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={form[k]} onChange={e => set(k, e.target.checked)} />
              {label}
            </label>
          ))}

          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="form-label">Final Yoast Rating</label>
            <select className="form-control" value={form.yoast_rating} onChange={e => set('yoast_rating', e.target.value)}>
              {YOAST_RATINGS.map(r => <option key={r.value} value={r.value}>{r.label || '—'}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Final URL (if slug changed)</label>
            <input className="form-control" value={form.final_url} onChange={e => set('final_url', e.target.value)} placeholder="https://..." />
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div style={{ padding: 16, borderTop: '1px solid var(--border-light)', display: 'flex', gap: 8, justifyContent: 'flex-end', flexShrink: 0 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving}>{saving ? 'Saving...' : (isEdit ? 'Save' : 'Add Page')}</button>
        </div>
      </div>
    </div>
  )
}

function YoastPageSidePanel({ client, profiles, page, onClose, onUpdated }) {
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const assignee = profiles.find(p => p.id === page.assigned_to)
  const rating = getRating(page.yoast_rating)

  const update = async (updates) => {
    setSaving(true)
    const { error } = await supabase.from('seo_pages').update(updates).eq('id', page.id)
    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    onUpdated()
  }

  const steps = [
    { key: 'alt_text_featured',    label: 'Alt text — featured image',     action: 'Mark alt text done (featured)' },
    { key: 'alt_text_all_images',  label: 'Alt text — all on-page images', action: 'Mark alt text done (all images)' },
    { key: 'description_featured', label: 'Description — featured image',  action: 'Mark featured description done' },
    { key: 'meta_description',     label: 'Meta description',              action: 'Mark meta description done' },
    { key: 'seo_title',            label: 'SEO title',                     action: 'Mark SEO title done' },
    { key: 'slug',                 label: 'Slug optimized',                action: 'Mark slug optimized' },
    { key: 'submitted_to_gsc',     label: 'Submitted to Google Search Console', action: 'Mark submitted to GSC' },
  ]
  const stepsDone = steps.filter(s => page[s.key]).length
  const allStepsDone = stepsDone === steps.length

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 90, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,43,74,0.3)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: 580, background: '#fff', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ background: 'var(--navy)', padding: '18px 22px', color: '#fff', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Yoast Page · {client.name}
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {page.is_urgent && <span style={{ marginRight: 6 }}>🔴</span>}
                {page.page_name}
              </div>
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>
                {page.keyword_choice && <>🔑 {page.keyword_choice} · </>}
                {assignee?.full_name || 'Unassigned'}
                {page.due_date && ` · Due ${safeFmt(page.due_date, 'MMM d')}`}
              </div>
            </div>
            <button className="btn-icon" style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff' }} onClick={onClose}><X size={16} /></button>
          </div>
          {page.yoast_rating && (
            <div style={{ marginTop: 10, fontSize: 12, background: 'rgba(255,255,255,0.15)', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 12 }}>
              Yoast rating: <strong>{rating.label}</strong>
            </div>
          )}
        </div>

        <div style={{ padding: '18px 22px', flex: 1, overflowY: 'auto' }}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Page Name</label>
              <input className="form-control" defaultValue={page.page_name || ''} onBlur={e => e.target.value !== (page.page_name || '') && update({ page_name: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Target Keyword</label>
              <input className="form-control" defaultValue={page.keyword_choice || ''} onBlur={e => e.target.value !== (page.keyword_choice || '') && update({ keyword_choice: e.target.value || null })} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Page URL</label>
            <input className="form-control" defaultValue={page.page_url || ''} onBlur={e => e.target.value !== (page.page_url || '') && update({ page_url: e.target.value || null })} placeholder="https://..." />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Assignee</label>
              <select className="form-control" value={page.assigned_to || ''} onChange={e => update({ assigned_to: e.target.value || null })}>
                <option value="">Unassigned</option>
                {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input className="form-control" type="date" value={page.due_date || ''} onChange={e => update({ due_date: e.target.value || null })} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Loom walkthrough (optional)</label>
            <input className="form-control" defaultValue={page.loom_url || ''} onBlur={e => e.target.value !== (page.loom_url || '') && update({ loom_url: e.target.value || null })} placeholder="https://www.loom.com/share/..." />
            {page.loom_url && (
              <a href={page.loom_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--teal)', marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <ExternalLink size={11} /> Watch Loom walkthrough
              </a>
            )}
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Category</label>
              <input className="form-control" defaultValue={page.category || ''} onBlur={e => e.target.value !== (page.category || '') && update({ category: e.target.value || null })} placeholder="e.g. Main Pages, Landing Pages" />
            </div>
            <div className="form-group">
              <label className="form-label">Final Yoast Rating</label>
              <select className="form-control" value={page.yoast_rating || ''} onChange={e => update({ yoast_rating: e.target.value || null })}>
                {YOAST_RATINGS.map(r => <option key={r.value} value={r.value}>{r.label || '—'}</option>)}
              </select>
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, marginBottom: 16 }}>
            <input type="checkbox" checked={page.is_urgent || false} onChange={e => update({ is_urgent: e.target.checked })} />
            🔴 Mark as urgent
          </label>

          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 20, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Optimization Steps</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: allStepsDone ? '#2e6e3a' : 'var(--text-muted)' }}>
              {stepsDone} / {steps.length} done
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {steps.map(step => (
              <WorkflowStep
                key={step.key}
                done={!!page[step.key]}
                label={step.label}
                actionLabel={step.action}
                onClick={() => update({ [step.key]: !page[step.key] })}
              />
            ))}
          </div>

          <div style={{ marginTop: 18 }}>
            {page.yoast_rating !== 'green' ? (
              <button className="btn btn-gold" style={{ width: '100%' }} disabled={!allStepsDone || saving}
                onClick={() => update({ yoast_rating: 'green' })}>
                ✅ Mark this page fully optimized (green)
              </button>
            ) : (
              <button className="btn btn-ghost" style={{ width: '100%' }} onClick={() => update({ yoast_rating: null })}>
                ↺ Reopen — clear green rating
              </button>
            )}
            {!allStepsDone && page.yoast_rating !== 'green' && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, textAlign: 'center' }}>
                Complete all the steps above first.
              </div>
            )}
          </div>

          <div className="form-group" style={{ marginTop: 20 }}>
            <label className="form-label">Final URL (if slug changed)</label>
            <input className="form-control" defaultValue={page.final_url || ''} placeholder="https://..." onBlur={e => e.target.value !== (page.final_url || '') && update({ final_url: e.target.value || null })} />
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={3} defaultValue={page.notes || ''} onBlur={e => e.target.value !== (page.notes || '') && update({ notes: e.target.value || null })} />
          </div>
        </div>
      </div>
    </div>
  )
}

function ProjectsTab({ client, projects, templates, profiles, currentUser, onReload, autoOpenProjectId, onClearAutoOpenProjectId }) {
  const [showNew, setShowNew] = useState(false)
  const [activeProject, setActiveProject] = useState(null)

  useEffect(() => {
    if (autoOpenProjectId && projects.length > 0) {
      const found = projects.find(p => p.id === autoOpenProjectId)
      if (found) {
        setActiveProject(found)
        onClearAutoOpenProjectId && onClearAutoOpenProjectId()
      }
    }
  }, [autoOpenProjectId, projects, onClearAutoOpenProjectId])

  if (activeProject) {
    return <ProjectDetail
      project={activeProject}
      template={templates.find(t => t.id === activeProject.template_id)}
      profiles={profiles}
      currentUser={currentUser}
      clients={[client]}
      onBack={() => { setActiveProject(null); onReload() }}
      onReload={async () => {
        const { data } = await supabase.from('seo_projects').select('*, items:seo_project_items(*)').eq('id', activeProject.id).single()
        if (data) setActiveProject(data)
        onReload()
      }}
    />
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button className="btn btn-gold" onClick={() => setShowNew(true)}><Plus size={14} /> New Project</button>
      </div>

      {projects.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize: 48, marginBottom: 12 }}>📚</div>
          <h3>No SEO projects yet</h3>
          <p>Create a project to batch up keyword pages, location pages, audits, and more.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {projects.map(p => {
            const tpl = templates.find(t => t.id === p.template_id)
            const items = p.items || []
            const itemsDone = items.filter(i => i.status === 'complete').length
            const isComplete = p.status === 'complete'
            const isArchived = p.status === 'archived'
            return (
              <div key={p.id} className="card" style={{ cursor: 'pointer', opacity: isArchived ? 0.7 : 1, borderLeft: `4px solid ${isComplete ? '#2e6e3a' : isArchived ? '#94a3b8' : 'var(--gold)'}` }}
                onClick={() => setActiveProject(p)}>
                <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{p.name}</div>
                    <div style={{ display: 'flex', gap: 10, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                      {tpl && <span>{tpl.name}</span>}
                      <span>{itemsDone}/{items.length} items done</span>
                      {p.due_date && <span>Due {safeFmt(p.due_date, 'MMM d')}</span>}
                      {p.score_start !== null && p.score_finish !== null && p.score_start !== undefined && p.score_finish !== undefined && (
                        <span>Score {p.score_start} → {p.score_finish}</span>
                      )}
                    </div>
                  </div>
                  <span className="status-pill" style={{ background: isComplete ? '#e8f5eb' : isArchived ? '#f1f5f9' : '#fff8e1', color: isComplete ? '#2e6e3a' : isArchived ? '#64748b' : '#8a6000' }}>
                    {isComplete ? '✓ Complete' : isArchived ? '📦 Archived' : p.status === 'on_hold' ? '⏸ On hold' : '▶ Active'}
                  </span>
                  <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
                </div>
              </div>
            )
          })}
        </div>
      )}

      {showNew && (
        <NewProjectModal
          client={client}
          templates={templates}
          currentUser={currentUser}
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); onReload() }}
        />
      )}
    </div>
  )
}

function NewProjectModal({ client, templates, currentUser, onClose, onSaved }) {
  const toast = useToast()
  const [templateId, setTemplateId] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [templatePageUrl, setTemplatePageUrl] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [scoreStart, setScoreStart] = useState('')
  const [saving, setSaving] = useState(false)

  const template = templates.find(t => t.id === templateId)
  const isAudit = template?.category === 'audit'

  const save = async () => {
    if (!templateId) { toast('Pick a template', 'error'); return }
    if (!name.trim()) { toast('Name required', 'error'); return }
    setSaving(true)
    try {
      const { data: project, error: projErr } = await supabase.from('seo_projects').insert({
        client_id: client.id,
        template_id: templateId,
        name: name.trim(),
        description: description || null,
        template_page_url: templatePageUrl || null,
        score_start: scoreStart ? parseInt(scoreStart) : null,
        due_date: dueDate || null,
        assigned_by: currentUser.id,
        status: 'active',
      }).select().single()
      if (projErr) throw projErr

      const { data: defaultItems } = await supabase.from('seo_project_template_items').select('*').eq('template_id', templateId).order('sort_order')
      if (defaultItems && defaultItems.length > 0) {
        const itemsToInsert = defaultItems.map(di => ({
          project_id: project.id,
          topic: di.default_topic,
          notes: di.default_notes,
          sort_order: di.sort_order,
          status: 'not_started',
        }))
        await supabase.from('seo_project_items').insert(itemsToInsert)
      }
      toast('Project created!', 'success')
      onSaved()
    } catch (e) {
      toast('Error: ' + e.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span className="modal-title">New SEO Project</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Template *</label>
            <select className="form-control" value={templateId} onChange={e => {
              setTemplateId(e.target.value)
              const t = templates.find(x => x.id === e.target.value)
              if (t && !name) {
                const monthYear = format(new Date(), 'MMM yyyy')
                setName(t.category === 'audit' ? `${t.name} — ${monthYear}` : `${t.name} — ${monthYear}`)
              }
            }}>
              <option value="">Pick a template...</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}{t.is_recurring ? ' (recurring)' : ''}</option>)}
            </select>
            {template && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{template.description}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Project Name *</label>
            <input className="form-control" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Target Keywords — Mar 2026" />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-control" rows={2} value={description} onChange={e => setDescription(e.target.value)} />
          </div>

          {template && !isAudit && template.uses_keyword && (
            <div className="form-group">
              <label className="form-label">Template Page URL (for reference layout)</label>
              <input className="form-control" value={templatePageUrl} onChange={e => setTemplatePageUrl(e.target.value)} placeholder="https://..." />
            </div>
          )}

          <div className="form-row">
            {isAudit && (
              <div className="form-group">
                <label className="form-label">AHREF Score at Start</label>
                <input className="form-control" type="number" value={scoreStart} onChange={e => setScoreStart(e.target.value)} placeholder="e.g. 35" />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input className="form-control" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving}>{saving ? 'Creating...' : '🚀 Create Project'}</button>
        </div>
      </div>
    </div>
  )
}

function ProjectDetail({ project, template, profiles, currentUser, clients, onBack, onReload }) {
  const toast = useToast()
  const [showAdd, setShowAdd] = useState(false)
  const [showBulkAdd, setShowBulkAdd] = useState(false)
  const [selectedItemIds, setSelectedItemIds] = useState([])
  const [editingItem, setEditingItem] = useState(null)
  const items = project.items || []
  const isAudit = template?.category === 'audit'

  const toggleField = async (item, field) => {
    const next = !item[field]
    await supabase.from('seo_project_items').update({ [field]: next }).eq('id', item.id)
    onReload()
  }
  const setItemField = async (item, field, value) => {
    await supabase.from('seo_project_items').update({ [field]: value || null }).eq('id', item.id)
    onReload()
  }
  const updateProject = async (updates) => {
    await supabase.from('seo_projects').update(updates).eq('id', project.id)
    onReload()
  }

  const allItemsDone = items.length > 0 && items.every(i => i.status === 'complete' || (i.approval_ad && i.optimized_in_yoast))
  const markComplete = async () => {
    const updates = { status: 'complete', completed_at: new Date().toISOString() }
    if (isAudit) {
      const finish = window.prompt('AHREF score at finish?', project.score_finish || '')
      if (finish !== null && finish !== '') updates.score_finish = parseInt(finish)
    }
    await updateProject(updates)
    toast('Project marked complete', 'success')
  }

  const reopen = async () => {
    await updateProject({ status: 'active', completed_at: null })
    toast('Project reopened', 'success')
  }

  const deleteProject = async () => {
    if (!window.confirm(`Delete project "${project.name}" and all its items?`)) return
    await supabase.from('seo_projects').delete().eq('id', project.id)
    toast('Project deleted', 'success')
    onBack()
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}><ArrowLeft size={14} /> Back to projects</button>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: 'var(--navy)' }}>{project.name}</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {template && <>{template.name} · </>}
            {items.filter(i => i.status === 'complete').length}/{items.length} items complete
          </div>
        </div>
        {project.status === 'archived' && (
          <span className="status-pill" style={{ background: '#f1f5f9', color: '#64748b' }}>📦 Archived — off everyone's to-do list</span>
        )}
        {project.status !== 'complete' && project.status !== 'archived' ? (
          <button className="btn btn-gold btn-sm" onClick={markComplete}><CheckCircle size={13} /> Mark Complete</button>
        ) : (
          <button className="btn btn-ghost btn-sm" onClick={reopen}>↺ Reopen</button>
        )}
        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={deleteProject}><Trash2 size={13} /></button>
      </div>

      {isAudit && (
        <div className="card" style={{ marginBottom: 16, background: 'var(--cream)' }}>
          <div className="card-body" style={{ display: 'flex', gap: 16, alignItems: 'center', fontSize: 13 }}>
            <span><strong>AHREF Score:</strong> {project.score_start ?? '—'} → {project.score_finish ?? '—'}</span>
            {project.status === 'complete' && project.completed_at && <span style={{ color: 'var(--text-muted)' }}>Completed {safeFmt(project.completed_at, 'MMM d, yyyy')}</span>}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => setShowBulkAdd(true)}><Plus size={13} /> Bulk Add</button>
        <button className="btn btn-ghost btn-sm" onClick={() => setShowAdd(true)}><Plus size={13} /> Add Item</button>
      </div>

      {items.length === 0 ? (
        <div className="empty-state">
          <h3>No items yet</h3>
          <p>{isAudit ? 'Add each issue you find from the audit (e.g. "Missing meta descriptions") with a link to the sheet of affected pages.' : 'Add items to this project.'}</p>
          <button className="btn btn-gold" onClick={() => setShowAdd(true)} style={{ marginTop: 12 }}><Plus size={13} /> Add First Item</button>
        </div>
      ) : (
        <>
          {selectedItemIds.length > 0 && (
            <div style={{ background: 'var(--navy)', color: '#fff', padding: '10px 16px', borderRadius: 10, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
              <span style={{ fontWeight: 600 }}>{selectedItemIds.length} selected</span>
              <span style={{ opacity: 0.6 }}>·</span>
              <span>Bulk assign to:</span>
              <select onChange={async (e) => {
                if (!e.target.value) return
                await supabase.from('seo_project_items').update({ assigned_to: e.target.value }).in('id', selectedItemIds)
                toast(`Assigned ${selectedItemIds.length} items`, 'success')
                setSelectedItemIds([])
                onReload()
              }}
                value=""
                style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', padding: '4px 8px', borderRadius: 6, fontSize: 12 }}>
                <option value="">Pick a person...</option>
                {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id} style={{ color: '#000' }}>{p.full_name}</option>)}
              </select>
              <button onClick={() => setSelectedItemIds([])} style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                Clear
              </button>
            </div>
          )}

          <div className="card" style={{ overflowX: 'auto' }}>
            <table className="task-table" style={{ width: '100%', minWidth: 700 }}>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input type="checkbox"
                      checked={selectedItemIds.length === items.length && items.length > 0}
                      onChange={e => setSelectedItemIds(e.target.checked ? items.map(i => i.id) : [])} />
                  </th>
                  <th>{isAudit ? 'Issue / Topic' : (template?.uses_topic ? 'Topic' : 'Keyword')}</th>
                  {!isAudit && template?.uses_keyword && template?.uses_topic && <th>Keyword</th>}
                  <th>Assignee</th>
                  <th>Due</th>
                  <th>{isAudit ? 'Issue Sheet' : 'Status'}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(item => {
                  const steps = []
                  if (template?.uses_ad_approval) steps.push(item.approval_ad)
                  if (template?.uses_client_approval) steps.push(item.approval_client)
                  if (template?.uses_yoast_optimize) steps.push(item.optimized_in_yoast)
                  if (template?.uses_gsc_submit) steps.push(item.submitted_to_gsc)
                  const stepsDone = steps.filter(Boolean).length
                  const isComplete = item.status === 'complete'

                  return (
                    <tr key={item.id} style={{ cursor: 'pointer', background: isComplete ? '#f6fbf7' : undefined }}
                      onClick={(e) => {
                        const tag = e.target.tagName
                        if (['INPUT', 'SELECT', 'BUTTON', 'TEXTAREA'].includes(tag)) return
                        setEditingItem(item)
                      }}>
                      <td onClick={e => e.stopPropagation()}>
                        <input type="checkbox"
                          checked={selectedItemIds.includes(item.id)}
                          onChange={e => setSelectedItemIds(ids => e.target.checked ? [...ids, item.id] : ids.filter(x => x !== item.id))} />
                      </td>
                      <td>
                        <div style={{ fontSize: 13, fontWeight: isComplete ? 400 : 500, color: isComplete ? 'var(--text-muted)' : 'var(--text-primary)', textDecoration: isComplete ? 'line-through' : 'none' }}>
                          {item.topic || item.keyword || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>(untitled)</span>}
                        </div>
                        {(() => {
                          const q2t = getQ2StepType(item, project)
                          // Hide the seeded grey description on Q2 PARENT steps —
                          // it's redundant with the right-panel directions. Keep it
                          // for blog children (holds the brief) and non-Q2 items.
                          const hideNotes = q2t && q2t !== 'blog_child'
                          if (hideNotes || !item.notes) return null
                          return <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2 }}>{item.notes}</div>
                        })()}
                        {!isAudit && template?.uses_q_and_a && (item.faq_questions || item.topics_to_include || item.structural_guidelines) && (
                          <div style={{ fontSize: 10.5, color: 'var(--teal)', marginTop: 2 }}>📝 Has content guidance</div>
                        )}
                      </td>
                      {!isAudit && template?.uses_keyword && template?.uses_topic && (
                        <td>
                          <input value={item.keyword || ''} onChange={e => setItemField(item, 'keyword', e.target.value)}
                            style={{ width: '100%', minWidth: 110, padding: '4px 6px', border: '1px solid transparent', borderRadius: 4, fontSize: 12.5, background: 'transparent' }}
                            onFocus={e => e.target.style.border = '1px solid var(--border)'}
                            onBlur={e => e.target.style.border = '1px solid transparent'} />
                        </td>
                      )}
                      <td>
                        <select value={item.assigned_to || ''} onChange={e => setItemField(item, 'assigned_to', e.target.value)}
                          style={{ fontSize: 11.5, padding: '3px 6px', border: '1px solid var(--border-light)', borderRadius: 6, maxWidth: 110 }}>
                          <option value="">Unassigned</option>
                          {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                        </select>
                      </td>
                      <td>
                        <input type="date" value={item.due_date || ''} onChange={e => setItemField(item, 'due_date', e.target.value)}
                          style={{ fontSize: 11.5, padding: '3px 6px', border: '1px solid var(--border-light)', borderRadius: 6 }} />
                      </td>
                      <td>
                        {isAudit ? (
                          item.final_url ? <a href={item.final_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'var(--teal)', fontSize: 12 }}>↗ Sheet</a> : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                        ) : isComplete ? (
                          <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, background: '#e8f5eb', color: '#2e6e3a', fontWeight: 600 }}>✓ Complete</span>
                        ) : (
                          <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, background: stepsDone === 0 ? 'var(--cream-dark)' : '#fff8e1', color: stepsDone === 0 ? 'var(--text-muted)' : '#8a6000', fontWeight: 600 }}>
                            {stepsDone}/{steps.length} steps
                          </span>
                        )}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <button className="btn-icon" onClick={async () => {
                          if (!window.confirm('Delete this item?')) return
                          await supabase.from('seo_project_items').delete().eq('id', item.id)
                          onReload()
                        }}><Trash2 size={12} /></button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showAdd && (
        <AddProjectItemModal
          project={project}
          template={template}
          profiles={profiles}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); onReload() }}
        />
      )}
      {showBulkAdd && (
        <BulkAddItemsModal
          project={project}
          template={template}
          profiles={profiles}
          onClose={() => setShowBulkAdd(false)}
          onSaved={() => { setShowBulkAdd(false); onReload() }}
        />
      )}
      {editingItem && (
        <ProjectItemSidePanel
          item={items.find(i => i.id === editingItem.id) || editingItem}
          project={project}
          template={template}
          clients={clients}
          profiles={profiles}
          currentUser={currentUser}
          onClose={() => setEditingItem(null)}
          onUpdated={onReload}
        />
      )}
    </div>
  )
}

function AddProjectItemModal({ project, template, profiles, onClose, onSaved }) {
  const toast = useToast()
  const isAudit = template?.category === 'audit'
  const [form, setForm] = useState({
    keyword: '', topic: '', notes: '',
    faq_questions: '', topics_to_include: '', structural_guidelines: '',
    assigned_to: '', due_date: '', page_or_blog: 'page',
    final_url: '',
  })
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const save = async () => {
    if (!form.keyword.trim() && !form.topic.trim()) {
      toast(isAudit ? 'Describe the issue' : 'Enter a keyword or topic', 'error')
      return
    }
    setSaving(true)
    const payload = {
      project_id: project.id,
      keyword: form.keyword || null,
      topic: form.topic || null,
      faq_questions: form.faq_questions || null,
      topics_to_include: form.topics_to_include || null,
      structural_guidelines: form.structural_guidelines || null,
      notes: form.notes || null,
      assigned_to: form.assigned_to || null,
      due_date: form.due_date || null,
      page_or_blog: form.page_or_blog,
      final_url: form.final_url || null,
      sort_order: (project.items?.length || 0) * 10 + 10,
      status: 'not_started',
    }
    const { error } = await supabase.from('seo_project_items').insert(payload)
    if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }
    toast('Item added', 'success')
    onSaved()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 540 }}>
        <div className="modal-header">
          <span className="modal-title">{isAudit ? 'Add Audit Issue' : 'Add Project Item'}</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {isAudit ? (
            <>
              <div className="form-group">
                <label className="form-label">Issue Topic *</label>
                <input className="form-control" value={form.topic} onChange={e => set('topic', e.target.value)} placeholder="e.g. Missing meta descriptions, Broken internal links" />
              </div>
              <div className="form-group">
                <label className="form-label">Link to issue sheet</label>
                <input className="form-control" value={form.final_url} onChange={e => set('final_url', e.target.value)} placeholder="Google Sheet URL listing all affected pages" />
              </div>
            </>
          ) : (
            <>
              {template?.uses_topic && (
                <div className="form-group">
                  <label className="form-label">Topic / Section</label>
                  <input className="form-control" value={form.topic} onChange={e => set('topic', e.target.value)} placeholder="e.g. Anxiety, Burnout, Anger" />
                </div>
              )}
              {template?.uses_keyword && (
                <div className="form-group">
                  <label className="form-label">Keyword</label>
                  <input className="form-control" value={form.keyword} onChange={e => set('keyword', e.target.value)} placeholder="e.g. anxiety therapist near me" />
                </div>
              )}
              {template?.uses_q_and_a && (
                <>
                  <div className="form-group">
                    <label className="form-label">FAQ Questions</label>
                    <textarea className="form-control" rows={3} value={form.faq_questions} onChange={e => set('faq_questions', e.target.value)} placeholder="What is X?&#10;How does Y work?&#10;..." />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Topics to Include</label>
                    <textarea className="form-control" rows={3} value={form.topics_to_include} onChange={e => set('topics_to_include', e.target.value)} placeholder="Important content/topics to cover on this page..." />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Structural Guidelines</label>
                    <textarea className="form-control" rows={3} value={form.structural_guidelines} onChange={e => set('structural_guidelines', e.target.value)} placeholder="Page structure notes — H1, sections, CTAs, length, etc." />
                  </div>
                </>
              )}
            </>
          )}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Assigned To</label>
              <select className="form-control" value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
                <option value="">Unassigned</option>
                {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input className="form-control" type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} />
            </div>
          </div>
          {!isAudit && (
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-control" value={form.page_or_blog} onChange={e => set('page_or_blog', e.target.value)}>
                <option value="page">Page</option>
                <option value="blog">Blog</option>
              </select>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving}>{saving ? 'Adding...' : 'Add Item'}</button>
        </div>
      </div>
    </div>
  )
}

function AuditsTab({ projects }) {
  const audits = projects.filter(p => p.template_id === QUARTERLY_AUDIT_TEMPLATE_ID).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

  if (audits.length === 0) {
    return (
      <div className="empty-state">
        <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
        <h3>No audits yet</h3>
        <p>Quarterly audits will appear here once you create them in the Projects tab.</p>
      </div>
    )
  }

  return (
    <div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>
        Quarterly audits should be done every 90 days. The most recent audit shows at the top.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {audits.map(a => {
          const isComplete = a.status === 'complete'
          return (
            <div key={a.id} className="card" style={{ borderLeft: `4px solid ${isComplete ? '#2e6e3a' : 'var(--gold)'}` }}>
              <div className="card-body" style={{ padding: '14px 18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{a.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      Started {safeFmt(a.created_at, 'MMM d, yyyy')}
                      {isComplete && a.completed_at && ` · Completed ${safeFmt(a.completed_at, 'MMM d, yyyy')}`}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {a.score_start !== null && a.score_start !== undefined && (
                      <div style={{ fontSize: 13 }}>
                        AHREF: <strong>{a.score_start}</strong> → <strong>{a.score_finish ?? '—'}</strong>
                      </div>
                    )}
                    <span className="status-pill" style={{ background: isComplete ? '#e8f5eb' : '#fff8e1', color: isComplete ? '#2e6e3a' : '#8a6000', fontSize: 11, marginTop: 4, display: 'inline-block' }}>
                      {isComplete ? '✓ Complete' : '▶ In progress'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function BulkAddItemsModal({ project, template, profiles, onClose, onSaved }) {
  const toast = useToast()
  const isAudit = template?.category === 'audit'
  const blankRow = () => ({ topic: '', keyword: '', assigned_to: '', due_date: '', final_url: '' })
  const [rows, setRows] = useState([blankRow(), blankRow(), blankRow(), blankRow(), blankRow()])
  const [pasteValue, setPasteValue] = useState('')
  const [defaultAssignee, setDefaultAssignee] = useState('')
  const [defaultDueDate, setDefaultDueDate] = useState('')
  const [saving, setSaving] = useState(false)

  const updateRow = (idx, field, value) => {
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, [field]: value } : r))
  }
  const addMore = (n = 5) => {
    setRows(rs => [...rs, ...Array(n).fill(0).map(blankRow)])
  }
  const removeRow = (idx) => {
    setRows(rs => rs.length > 1 ? rs.filter((_, i) => i !== idx) : rs)
  }

  const applyPaste = () => {
    const lines = pasteValue.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0)
    if (lines.length === 0) { toast('Paste some lines first', 'error'); return }
    const parsed = lines.map(line => {
      const parts = line.includes('\t') ? line.split('\t') : line.split(',').map(p => p.trim())
      if (isAudit) {
        return { ...blankRow(), topic: parts[0] || '', final_url: parts[1] || '' }
      } else {
        const keyword = parts[0] || ''
        const topic = parts[1] || ''
        return { ...blankRow(), keyword, topic }
      }
    })
    setRows(parsed)
    setPasteValue('')
    toast(`Loaded ${parsed.length} rows from paste`, 'success')
  }

  const save = async () => {
    const filled = rows.filter(r => (r.topic && r.topic.trim()) || (r.keyword && r.keyword.trim()))
    if (filled.length === 0) {
      toast('Fill in at least one row', 'error')
      return
    }
    setSaving(true)
    const baseSort = (project.items?.length || 0) * 10 + 10
    const payload = filled.map((r, idx) => ({
      project_id: project.id,
      topic: r.topic || null,
      keyword: r.keyword || null,
      assigned_to: r.assigned_to || defaultAssignee || null,
      due_date: r.due_date || defaultDueDate || null,
      final_url: r.final_url || null,
      page_or_blog: 'page',
      sort_order: baseSort + idx * 10,
      status: 'not_started',
    }))
    const { error } = await supabase.from('seo_project_items').insert(payload)
    if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }
    toast(`Added ${payload.length} items!`, 'success')
    onSaved()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 760 }}>
        <div className="modal-header">
          <span className="modal-title">Bulk Add Items</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--cream)', padding: 14, borderRadius: 10, marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📋 Paste from spreadsheet (optional)</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 8 }}>
              {isAudit
                ? 'One issue per line. Optionally add a comma and the sheet URL. Example: "Missing meta descriptions, https://docs.google.com/..."'
                : 'One keyword per line. Optionally add a comma and topic. Example: "anxiety therapy, Anxiety"'}
            </div>
            <textarea rows={4} value={pasteValue} onChange={e => setPasteValue(e.target.value)}
              placeholder={isAudit ? 'Missing meta descriptions\nBroken links\n...' : 'anxiety therapy\ntrauma counseling\n...'}
              style={{ width: '100%', padding: 8, fontFamily: 'monospace', fontSize: 12, border: '1px solid var(--border)', borderRadius: 6 }} />
            <button className="btn btn-ghost btn-sm" onClick={applyPaste} style={{ marginTop: 6 }}>Load into rows below</button>
          </div>

          <div className="form-row" style={{ marginBottom: 12 }}>
            <div className="form-group">
              <label className="form-label">Default Assignee (for blank rows)</label>
              <select className="form-control" value={defaultAssignee} onChange={e => setDefaultAssignee(e.target.value)}>
                <option value="">— Unassigned —</option>
                {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Default Due Date</label>
              <input className="form-control" type="date" value={defaultDueDate} onChange={e => setDefaultDueDate(e.target.value)} />
            </div>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{rows.length} rows</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border-light)', borderRadius: 8, padding: 8 }}>
            {rows.map((r, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: isAudit ? '1fr 1fr 32px' : '1fr 1fr 1fr 32px', gap: 6, alignItems: 'center' }}>
                {isAudit ? (
                  <>
                    <input value={r.topic} onChange={e => updateRow(idx, 'topic', e.target.value)} placeholder="Issue topic" className="form-control" style={{ fontSize: 12, padding: '4px 8px' }} />
                    <input value={r.final_url} onChange={e => updateRow(idx, 'final_url', e.target.value)} placeholder="Sheet URL (optional)" className="form-control" style={{ fontSize: 12, padding: '4px 8px' }} />
                  </>
                ) : (
                  <>
                    <input value={r.keyword} onChange={e => updateRow(idx, 'keyword', e.target.value)} placeholder="Keyword" className="form-control" style={{ fontSize: 12, padding: '4px 8px' }} />
                    {template?.uses_topic && <input value={r.topic} onChange={e => updateRow(idx, 'topic', e.target.value)} placeholder="Topic (optional)" className="form-control" style={{ fontSize: 12, padding: '4px 8px' }} />}
                    <select value={r.assigned_to} onChange={e => updateRow(idx, 'assigned_to', e.target.value)} className="form-control" style={{ fontSize: 12, padding: '4px 8px' }}>
                      <option value="">— Use default —</option>
                      {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                    </select>
                  </>
                )}
                <button className="btn-icon" onClick={() => removeRow(idx)} title="Remove row"><X size={12} /></button>
              </div>
            ))}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => addMore(5)} style={{ marginTop: 8 }}><Plus size={12} /> Add 5 more rows</button>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving}>{saving ? 'Saving...' : `🚀 Add ${rows.filter(r => (r.topic && r.topic.trim()) || (r.keyword && r.keyword.trim())).length} Items`}</button>
        </div>
      </div>
    </div>
  )
}

function GuidanceEditorModal({ item, onClose, onSaved }) {
  const toast = useToast()
  const [faq, setFaq] = useState(item.faq_questions || '')
  const [topics, setTopics] = useState(item.topics_to_include || '')
  const [structural, setStructural] = useState(item.structural_guidelines || '')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    setSaving(true)
    const { error } = await supabase.from('seo_project_items').update({
      faq_questions: faq || null,
      topics_to_include: topics || null,
      structural_guidelines: structural || null,
    }).eq('id', item.id)
    if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }
    toast('Guidance saved', 'success')
    onSaved()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 620 }}>
        <div className="modal-header">
          <span className="modal-title">Content Guidance — {item.topic || item.keyword}</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">FAQ Questions</label>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>What questions should this page answer? One per line.</div>
            <textarea className="form-control" rows={5} value={faq} onChange={e => setFaq(e.target.value)} placeholder="What is X?&#10;How does Y work?&#10;..." />
          </div>
          <div className="form-group">
            <label className="form-label">Topics to Include</label>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>Important content/topics the page needs to cover.</div>
            <textarea className="form-control" rows={5} value={topics} onChange={e => setTopics(e.target.value)} placeholder="Cover: common symptoms, treatment options, when to seek help..." />
          </div>
          <div className="form-group">
            <label className="form-label">Structural Guidelines</label>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>Notes on page structure: H1, sections, CTAs, word count, internal links.</div>
            <textarea className="form-control" rows={5} value={structural} onChange={e => setStructural(e.target.value)} placeholder="H1 with primary keyword, 800-1200 words, link to /contact, include client testimonials..." />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving}>{saving ? 'Saving...' : '💾 Save Guidance'}</button>
        </div>
      </div>
    </div>
  )
}

function ProjectItemSidePanel({ item, project, template, clients, profiles, currentUser, onClose, onUpdated }) {
  const toast = useToast()
  const isAudit = template?.category === 'audit'
  const client = clients?.find(c => c.id === project.client_id)
  const [saving, setSaving] = useState(false)
  const assignee = profiles.find(p => p.id === item.assigned_to)
  const q2StepType = getQ2StepType(item, project)

  const update = async (updates) => {
    setSaving(true)
    const { error } = await supabase.from('seo_project_items').update(updates).eq('id', item.id)
    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    onUpdated()
  }

  const markCompleteAvailable = isAudit
    ? !!item.final_url
    : (template?.uses_ad_approval ? item.approval_ad : true) &&
      (template?.uses_client_approval ? item.approval_client : true) &&
      (template?.uses_yoast_optimize ? item.optimized_in_yoast : true) &&
      (template?.uses_gsc_submit ? item.submitted_to_gsc : true)

  const sendToClientEmail = () => {
    const clientName = client?.name || 'your practice'
    const subject = encodeURIComponent(`Review request: ${item.topic || item.keyword || 'new page'}`)
    const body = encodeURIComponent(
      `Hi,\n\nWe have a new page draft ready for your review.\n\n` +
      `Topic: ${item.topic || item.keyword || ''}\n` +
      (item.final_url ? `Preview link: ${item.final_url}\n` : '') +
      `\nPlease review and let us know:\n• ✅ Approved as-is\n• ✏️ Revisions needed\n\n` +
      `Best,\n${profiles.find(p => p.id === currentUser.id)?.full_name || 'LMR Team'}`
    )
    const clientEmail = client?.contact_email || ''
    window.open(`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(clientEmail)}&su=${subject}&body=${body}`, '_blank')
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 90, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,43,74,0.3)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: 580, background: '#fff', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ background: 'var(--navy)', padding: '18px 22px', color: '#fff', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {isAudit ? 'Audit Issue' : (template?.uses_topic ? 'Topic / Page' : 'Keyword / Page')} · {project.name}
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, marginTop: 4 }}>
                {item.topic || item.keyword || '(untitled)'}
              </div>
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>
                {client?.name}
                {assignee && ` · ${assignee.full_name}`}
                {item.due_date && ` · Due ${item.due_date}`}
              </div>
            </div>
            <button className="btn-icon" style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff' }} onClick={onClose}><X size={16} /></button>
          </div>
          {item.status === 'complete' && (
            <div style={{ marginTop: 10, fontSize: 12, background: 'rgba(255,255,255,0.15)', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 12 }}>
              ✓ Marked complete
            </div>
          )}
        </div>

        <div style={{ padding: '18px 22px', flex: 1, overflowY: 'auto' }}>
          {q2StepType ? (
            <Q2StepEditor
              stepType={q2StepType}
              item={item}
              project={project}
              client={client}
              profiles={profiles}
              currentUser={currentUser}
              onUpdated={onUpdated}
            />
          ) : (
            <>
              {!isAudit && template?.uses_keyword && (
                <div className="form-group">
                  <label className="form-label">Keyword</label>
                  <input className="form-control" defaultValue={item.keyword || ''} onBlur={e => e.target.value !== (item.keyword || '') && update({ keyword: e.target.value || null })} />
                </div>
              )}
              {template?.uses_topic && !isAudit && (
                <div className="form-group">
                  <label className="form-label">Topic</label>
                  <input className="form-control" defaultValue={item.topic || ''} onBlur={e => e.target.value !== (item.topic || '') && update({ topic: e.target.value || null })} />
                </div>
              )}
              {isAudit && (
                <div className="form-group">
                  <label className="form-label">Issue Topic</label>
                  <input className="form-control" defaultValue={item.topic || ''} onBlur={e => e.target.value !== (item.topic || '') && update({ topic: e.target.value || null })} placeholder="e.g. Missing meta descriptions" />
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Assignee</label>
                  <select className="form-control" value={item.assigned_to || ''} onChange={e => update({ assigned_to: e.target.value || null })}>
                    <option value="">Unassigned</option>
                    {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Due Date</label>
                  <input className="form-control" type="date" value={item.due_date || ''} onChange={e => update({ due_date: e.target.value || null })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{isAudit ? 'Link to Issue Sheet' : 'Final URL'}</label>
                <input className="form-control" defaultValue={item.final_url || ''} placeholder="https://..." onBlur={e => e.target.value !== (item.final_url || '') && update({ final_url: e.target.value || null })} />
              </div>

              {!isAudit && (template?.uses_ad_approval || template?.uses_client_approval || template?.uses_yoast_optimize || template?.uses_gsc_submit) && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 20, marginBottom: 10 }}>Workflow Steps</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {template?.uses_ad_approval && (
                      <ADApprovalStep
                        item={item}
                        hasAd={!!client?.account_director_id}
                        onSendToAd={async () => {
                          if (!client?.account_director_id) return
                          await update({ awaiting_ad_approval: true })
                          if (client.account_director_id !== currentUser.id) {
                            await supabase.from('notifications').insert({
                              user_id: client.account_director_id,
                              type: 'comment',
                              message: `SEO item needs your approval: "${item.topic || item.keyword || 'item'}" in ${project.name} (${client.name})`,
                            })
                          }
                          toast('Sent to AD for approval', 'success')
                        }}
                        onMarkApproved={() => update({ approval_ad: true, awaiting_ad_approval: false })}
                        onUndo={() => update({ approval_ad: false, awaiting_ad_approval: false })}
                      />
                    )}
                    {template?.uses_client_approval && (
                      <WorkflowStep
                        done={item.approval_client}
                        label="Client approval"
                        actionLabel="📨 Send to client to approve"
                        secondaryAction={item.approval_client ? null : sendToClientEmail}
                        onClick={() => update({ approval_client: !item.approval_client })}
                        secondaryLabel={item.approval_client ? null : 'Mark approved'}
                      />
                    )}
                    {template?.uses_yoast_optimize && (
                      <WorkflowStep
                        done={item.optimized_in_yoast}
                        label="Optimize in Yoast"
                        actionLabel="Mark optimized in Yoast"
                        onClick={() => update({ optimized_in_yoast: !item.optimized_in_yoast })}
                      />
                    )}
                    {template?.uses_gsc_submit && (
                      <WorkflowStep
                        done={item.submitted_to_gsc}
                        label="Submit to Google Search Console"
                        actionLabel="Mark submitted to GSC"
                        onClick={() => update({ submitted_to_gsc: !item.submitted_to_gsc })}
                      />
                    )}
                  </div>
                </>
              )}

              <div style={{ marginTop: 18 }}>
                {item.status !== 'complete' ? (
                  <button className="btn btn-gold" style={{ width: '100%' }} disabled={!markCompleteAvailable || saving} onClick={() => update({ status: 'complete' })}>
                    ✅ Mark this item complete
                  </button>
                ) : (
                  <button className="btn btn-ghost" style={{ width: '100%' }} onClick={() => update({ status: 'in_progress' })}>
                    ↺ Reopen this item
                  </button>
                )}
                {!markCompleteAvailable && item.status !== 'complete' && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, textAlign: 'center' }}>
                    {isAudit ? 'Add the issue sheet URL first.' : 'Complete all the steps above first.'}
                  </div>
                )}
              </div>

              {!isAudit && template?.uses_q_and_a && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 22, marginBottom: 10 }}>Content Guidance</div>
                  <div className="form-group">
                    <label className="form-label">FAQ Questions</label>
                    <textarea className="form-control" rows={4} defaultValue={item.faq_questions || ''} onBlur={e => e.target.value !== (item.faq_questions || '') && update({ faq_questions: e.target.value || null })} placeholder="What is X?&#10;How does Y work?" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Topics to Include</label>
                    <textarea className="form-control" rows={4} defaultValue={item.topics_to_include || ''} onBlur={e => e.target.value !== (item.topics_to_include || '') && update({ topics_to_include: e.target.value || null })} placeholder="Important content/topics to cover..." />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Structural Guidelines</label>
                    <textarea className="form-control" rows={4} defaultValue={item.structural_guidelines || ''} onBlur={e => e.target.value !== (item.structural_guidelines || '') && update({ structural_guidelines: e.target.value || null })} placeholder="H1, sections, CTAs, length..." />
                  </div>
                </>
              )}

              <div className="form-group" style={{ marginTop: 20 }}>
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={3} defaultValue={item.notes || ''} onBlur={e => e.target.value !== (item.notes || '') && update({ notes: e.target.value || null })} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function ADApprovalStep({ item, hasAd, onSendToAd, onMarkApproved, onUndo }) {
  const approved = item.approval_ad
  const awaiting = item.awaiting_ad_approval && !approved

  const bgColor = approved ? '#e8f5eb' : awaiting ? '#fff8e1' : 'var(--cream)'
  const borderColor = approved ? '#c2dec8' : awaiting ? '#f0d98a' : 'var(--border-light)'
  const labelColor = approved ? '#2e6e3a' : awaiting ? '#8a6000' : 'var(--text-primary)'

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 8 }}>
      <div style={{
        width: 22, height: 22, borderRadius: 6,
        background: approved ? '#2e6e3a' : '#fff', border: `1.5px solid ${approved ? '#2e6e3a' : 'var(--border)'}`,
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
        flexShrink: 0,
      }}>{approved ? '✓' : awaiting ? '⏳' : ''}</div>
      <div style={{ flex: 1, fontSize: 13, fontWeight: approved ? 500 : 600, color: labelColor }}>
        {approved ? '✓ AD approved' : awaiting ? '⏳ Awaiting AD approval' : 'AD approval'}
        {!hasAd && !approved && !awaiting && (
          <div style={{ fontSize: 10.5, fontWeight: 400, color: 'var(--text-muted)', marginTop: 2 }}>
            No AD set for this client — assign one in the client profile to enable "Send to AD."
          </div>
        )}
      </div>
      {approved ? (
        <button onClick={onUndo} style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', padding: '2px 6px', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>
          Undo
        </button>
      ) : awaiting ? (
        <>
          <button onClick={onMarkApproved} style={{ background: 'var(--navy)', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
            Mark approved
          </button>
          <button onClick={onUndo} style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', padding: '2px 6px', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>
            Cancel
          </button>
        </>
      ) : (
        <>
          {hasAd && (
            <button onClick={onSendToAd} style={{ background: 'var(--navy)', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
              📨 Send to AD
            </button>
          )}
          <button onClick={onMarkApproved} style={{ background: 'var(--cream-dark)', color: 'var(--text-primary)', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 500 }}>
            Mark approved
          </button>
        </>
      )}
    </div>
  )
}

function WorkflowStep({ done, label, actionLabel, secondaryAction, secondaryLabel, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
        background: done ? '#e8f5eb' : 'var(--cream)',
        border: `1px solid ${done ? '#c2dec8' : 'var(--border-light)'}`,
        borderRadius: 8,
        cursor: 'pointer',
      }}>
      <div style={{
        width: 22, height: 22, borderRadius: 6,
        background: done ? '#2e6e3a' : '#fff', border: `1.5px solid ${done ? '#2e6e3a' : 'var(--border)'}`,
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
        flexShrink: 0,
      }}>{done ? '✓' : ''}</div>
      <div style={{ flex: 1, fontSize: 13, fontWeight: done ? 500 : 600, color: done ? '#2e6e3a' : 'var(--text-primary)' }}>
        {done ? `✓ ${label}` : label}
      </div>
      {!done && secondaryAction && (
        <button onClick={e => { e.stopPropagation(); secondaryAction() }}
          style={{ background: 'var(--navy)', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
          {actionLabel}
        </button>
      )}
      {!done && !secondaryAction && (
        <button onClick={e => { e.stopPropagation(); onClick() }}
          style={{ background: 'var(--navy)', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
          {actionLabel}
        </button>
      )}
      {!done && secondaryAction && secondaryLabel && (
        <button onClick={e => { e.stopPropagation(); onClick() }}
          style={{ background: 'var(--cream-dark)', color: 'var(--text-primary)', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 500 }}>
          {secondaryLabel}
        </button>
      )}
      {done && (
        <button onClick={e => { e.stopPropagation(); onClick() }}
          style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', padding: '2px 6px', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>
          Undo
        </button>
      )}
    </div>
  )
}

export function BulkAddPagesModal({ client, profiles, onClose, onSaved }) {
  const toast = useToast()
  const blankRow = () => ({ page_name: '', keyword_choice: '', page_url: '' })
  const [rows, setRows] = useState([blankRow(), blankRow(), blankRow(), blankRow(), blankRow()])
  const [pasteValue, setPasteValue] = useState('')
  const [defaultAssignee, setDefaultAssignee] = useState('')
  const [category, setCategory] = useState('')
  const [saving, setSaving] = useState(false)

  const updateRow = (idx, field, value) => {
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, [field]: value } : r))
  }
  const addMore = (n = 5) => setRows(rs => [...rs, ...Array(n).fill(0).map(blankRow)])
  const removeRow = (idx) => setRows(rs => rs.length > 1 ? rs.filter((_, i) => i !== idx) : rs)

  const applyPaste = () => {
    const lines = pasteValue.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0)
    if (lines.length === 0) { toast('Paste some lines first', 'error'); return }
    const parsed = lines.map(line => {
      const parts = line.includes('\t') ? line.split('\t') : line.split(',').map(p => p.trim())
      return { ...blankRow(), page_name: parts[0] || '', keyword_choice: parts[1] || '', page_url: parts[2] || '' }
    })
    setRows(parsed)
    setPasteValue('')
    toast(`Loaded ${parsed.length} rows from paste`, 'success')
  }

  const save = async () => {
    const filled = rows.filter(r => r.page_name && r.page_name.trim())
    if (filled.length === 0) { toast('Add at least one page name', 'error'); return }
    setSaving(true)
    const payload = filled.map((r, idx) => ({
      client_id: client.id,
      page_name: r.page_name,
      keyword_choice: r.keyword_choice || null,
      page_url: r.page_url || null,
      category: category || null,
      assigned_to: defaultAssignee || null,
      sort_order: idx * 10,
    }))
    const { error } = await supabase.from('seo_pages').insert(payload)
    if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }
    toast(`Added ${payload.length} pages!`, 'success')
    onSaved()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 760 }}>
        <div className="modal-header">
          <span className="modal-title">Bulk Add Yoast Pages</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--cream)', padding: 14, borderRadius: 10, marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📋 Paste from spreadsheet (optional)</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 8 }}>
              One page per line. Format: "Page Name, Keyword, URL" (commas or tabs).
            </div>
            <textarea rows={4} value={pasteValue} onChange={e => setPasteValue(e.target.value)}
              placeholder="Home, online therapy, https://example.com/&#10;About, trauma informed, https://example.com/about/"
              style={{ width: '100%', padding: 8, fontFamily: 'monospace', fontSize: 12, border: '1px solid var(--border)', borderRadius: 6 }} />
            <button className="btn btn-ghost btn-sm" onClick={applyPaste} style={{ marginTop: 6 }}>Load into rows below</button>
          </div>

          <div className="form-row" style={{ marginBottom: 12 }}>
            <div className="form-group">
              <label className="form-label">Default Assignee</label>
              <select className="form-control" value={defaultAssignee} onChange={e => setDefaultAssignee(e.target.value)}>
                <option value="">— Unassigned —</option>
                {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <input className="form-control" value={category} onChange={e => setCategory(e.target.value)} placeholder="e.g. Main Pages" />
            </div>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{rows.length} rows</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border-light)', borderRadius: 8, padding: 8 }}>
            {rows.map((r, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr 32px', gap: 6, alignItems: 'center' }}>
                <input value={r.page_name} onChange={e => updateRow(idx, 'page_name', e.target.value)} placeholder="Page name" className="form-control" style={{ fontSize: 12, padding: '4px 8px' }} />
                <input value={r.keyword_choice} onChange={e => updateRow(idx, 'keyword_choice', e.target.value)} placeholder="Keyword" className="form-control" style={{ fontSize: 12, padding: '4px 8px' }} />
                <input value={r.page_url} onChange={e => updateRow(idx, 'page_url', e.target.value)} placeholder="URL (optional)" className="form-control" style={{ fontSize: 12, padding: '4px 8px' }} />
                <button className="btn-icon" onClick={() => removeRow(idx)} title="Remove row"><X size={12} /></button>
              </div>
            ))}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => addMore(5)} style={{ marginTop: 8 }}><Plus size={12} /> Add 5 more rows</button>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving}>{saving ? 'Saving...' : `🚀 Add ${rows.filter(r => r.page_name && r.page_name.trim()).length} Pages`}</button>
        </div>
      </div>
    </div>
  )
}